import { IItem, IStockOnHandLine, IStocktake, StocktakeTemplate } from '$models'
import dayjs, { Dayjs } from 'dayjs'
import { SharedSlice } from 'src/components/slice'
import { StateCreator } from 'zustand'
import { budgetService } from '$services'
import useAppStore from '$appStore'
import {
  calculateQuantityToOrderDetails,
  combineDateAndTime,
  convertCountingUnitToOrderingUnit,
  loadReportsHelper,
  transformToGroupedPredictedOrderLines,
  updateStockOnHandLineWithStocktake,
} from './helpers/helpers'
import { exceptionMessageHandler } from '$utils'
import { API_DATE_FORMAT } from '$constants'
import useStockStore from '../../store'
import {
  BudgetsByTemplateId,
  PosOrdersByOpeningAndClosingDate,
  StockOnHandReportsByDateAndTemplateId,
  StockReceivedByOpeningAndClosingDate,
} from './types'

export interface PredictedOrderUi {
  groupedPredictedOrderLines: GroupedPredictedOrderLines[]
  totalBudgetSalesAmount?: number
  totalSalesAmount?: number
  predictedOrderRequestParams?: Partial<PredictedOrderRequestParams>
}

export interface PredictedOrderRequestParams {
  stocktakeTemplateId: string
  salesModelStartDate?: Dayjs
  salesModelEndDate?: Dayjs
  orderStockDateUntil?: Dayjs
  additionalBuffer: number
}

export interface GroupedPredictedOrderLines {
  group: string
  lines: IPredictedOrderLine[]
  sortOrder?: number
}

export interface IPredictedOrderLine {
  lineId: string
  lineClass: () => string
  stockItemName: string
  stockOnHandLine: IStockOnHandLine
  quantityUsed: number
  predictedUsage: number
  isDeleted: boolean
  initialCurrentQuantityToOrder?: number
  currentQuantityToOrder?: number
  sortOrder?: number
}

export interface PredictedOrderApiResponses {
  stockOnHandReportApiResponses?: StockOnHandReportsByDateAndTemplateId
  stockReceivedApiResponses?: StockReceivedByOpeningAndClosingDate
  budgetsApiResponses?: BudgetsByTemplateId
  posOrdersApiResponses?: PosOrdersByOpeningAndClosingDate
  shouldRevalidate?: boolean
}

export interface PredictedOrderSlice extends PredictedOrderState {
  resetPredictedOrderSlice: () => void

  resetPredictedOrderApiResponses: () => void

  populatePredictedOrder: (
    salesModelStartDate: dayjs.Dayjs,
    salesModelEndDate: dayjs.Dayjs,
    additionalBuffer: number,
    stocktakeTemplateId: string,
    itemId?: string
  ) => Promise<void>

  updatePredictedOrderBudget: (
    orderStockDateUntil: dayjs.Dayjs
  ) => Promise<void>

  updateIsLineDeleted: (lineId: string, deleted: boolean) => void

  updateMinimumStock: (lineId: string, updatedItem: IItem) => Promise<void>

  updateAllQuantitiesToOrder: (useMinimumStock: boolean) => void

  updateQuantityToOrder: (
    lineId: string,
    currentQuantityToOrder: number
  ) => void

  addStockcount: (
    newStockTake: IStocktake,
    currentPredictedOrderLine: IPredictedOrderLine
  ) => Promise<void>
}

interface PredictedOrderState {
  predictedOrderUi?: PredictedOrderUi
  stocktakeTemplates?: StocktakeTemplate[]
  predictedOrderApiResponses?: PredictedOrderApiResponses
}

const initialPredictedOrderState: PredictedOrderState = {
  predictedOrderUi: undefined,
  stocktakeTemplates: undefined,
  predictedOrderApiResponses: undefined,
}

const createPredictedOrderSlice: StateCreator<
  PredictedOrderSlice & SharedSlice,
  [],
  [],
  PredictedOrderSlice
> = (set, get) => ({
  ...initialPredictedOrderState,

  resetPredictedOrderSlice: () => set(initialPredictedOrderState),

  resetPredictedOrderApiResponses: () =>
    set(() => ({
      predictedOrderApiResponses: undefined,
    })),

  updatePredictedOrderBudget: async (orderStockDateUntil: dayjs.Dayjs) => {
    const { reportsMetadata } = useAppStore.getState()

    const budgetFrom = combineDateAndTime(
      dayjs(),
      reportsMetadata.startDateTime
    ).format(API_DATE_FORMAT)

    const budgetTo = combineDateAndTime(
      orderStockDateUntil,
      reportsMetadata.startDateTime
    ).format(API_DATE_FORMAT)

    const budgetsAPIResponsesDict =
      get().predictedOrderApiResponses?.budgetsApiResponses ?? {}

    // Use cached budgets if available, otherwise fetch from the service
    const budgets = budgetsAPIResponsesDict[budgetTo]
      ? budgetsAPIResponsesDict[budgetTo]
      : await budgetService.getBudgets(budgetFrom, budgetTo)

    const totalBudgetSalesAmount = budgets?.reduce(
      (sum, budget) => sum + (budget.amount ?? 0),
      0
    )

    set((state) => {
      const prevPredictedOrderUi = state.predictedOrderUi ?? {
        groupedPredictedOrderLines: [],
      }

      return {
        predictedOrderUi: {
          ...prevPredictedOrderUi,
          totalBudgetSalesAmount,
          predictedOrderRequestParams: {
            ...prevPredictedOrderUi.predictedOrderRequestParams,
            orderStockDateUntil: orderStockDateUntil,
          },
        },

        predictedOrderApiResponses: {
          ...state.predictedOrderApiResponses,
          budgetsApiResponses: {
            ...state.predictedOrderApiResponses?.budgetsApiResponses,
            [budgetTo]: budgets,
          },
        },
      }
    })
  },

  populatePredictedOrder: async (
    salesModelStartDate,
    salesModelEndDate,
    additionalBuffer,
    stocktakeTemplateId,
    itemId
  ) => {
    const { items, units } = useStockStore.getState()
    const { reportsMetadata } = useAppStore.getState()

    // LOADED-5954: We need to run a stock on hand report for each of
    // the sales model period start and end date, using the company start time (reportsMetadata.startDateTime)
    const stockOnHandOpeningDateTime = combineDateAndTime(
      salesModelStartDate,
      reportsMetadata.startDateTime
    )

    const openingDateKey = stockOnHandOpeningDateTime.format(API_DATE_FORMAT)

    const stockOnHandClosingDateTime = combineDateAndTime(
      salesModelEndDate,
      reportsMetadata.startDateTime
    )

    const closingDateKey = stockOnHandClosingDateTime.format(API_DATE_FORMAT)

    const currentDateTime = combineDateAndTime(
      dayjs(),
      reportsMetadata.startDateTime
    )

    const currentDateKey = currentDateTime.format(API_DATE_FORMAT)

    const reports = await loadReportsHelper({
      get,
      set,
      currentDateTime,
      stockOnHandOpeningDateTime,
      stockOnHandClosingDateTime,
      currentDateKey,
      openingDateKey,
      closingDateKey,
      stocktakeTemplateId,
      itemId,
    })

    if (!reports) return

    const {
      currentStockOnHandReport,
      openingStockOnHandReport,
      closingStockOnHandReport,
      stockReceived,
      posOrders,
    } = reports

    const totalSalesAmount =
      posOrders?.reduce((sum, posOrder) => sum + (posOrder.amount ?? 0), 0) ?? 0

    const currentStockOnHandLines = currentStockOnHandReport?.lines?.map(
      (line) => {
        const item = line.itemId ? items[line.itemId] : undefined

        const quantityOnHand =
          line.quantityOnHand &&
          line.countingUnitRatio &&
          item?.orderingUnitRatio &&
          // The quantityOnHand needs to be converted to counting unit in all reports
          convertCountingUnitToOrderingUnit(
            line.quantityOnHand,
            line.countingUnitRatio,
            item.orderingUnitRatio
          )

        return {
          ...line,
          quantityOnHand,
        }
      }
    )

    // We only need a dictonary to find the opening quantity on hand later
    const openingQuantityOnHandDict = openingStockOnHandReport?.lines?.reduce<
      Record<string, number>
    >((acc, line) => {
      if (line.itemId) {
        const item = items[line.itemId]

        if (
          line.quantityOnHand &&
          line.countingUnitRatio &&
          item?.orderingUnitRatio
        ) {
          acc[line.itemId] = convertCountingUnitToOrderingUnit(
            line.quantityOnHand,
            line.countingUnitRatio,
            item.orderingUnitRatio
          )
        }
      }

      return acc
    }, {})

    // We only need a dictonary to find the closing quantity on hand later
    const closingQuantityOnHandDict = closingStockOnHandReport?.lines?.reduce<
      Record<string, number>
    >((acc, line) => {
      if (line.itemId) {
        const item = items[line.itemId]

        if (
          line.quantityOnHand &&
          line.countingUnitRatio &&
          item?.orderingUnitRatio
        ) {
          acc[line.itemId] = convertCountingUnitToOrderingUnit(
            line.quantityOnHand,
            line.countingUnitRatio,
            item.orderingUnitRatio
          )
        }
      }

      return acc
    }, {})

    const quantityReceivedDict = stockReceived?.reduce<Record<string, number>>(
      (acc, stockReceived) => {
        stockReceived.lines?.forEach((line) => {
          if (!line.itemId) return

          // We are working with multiple lines of stock received (Purchase Orders and Invoices)
          // the same itemId can be present in more than one line
          const prev = acc[line.itemId] ?? 0

          const item = items[line.itemId]

          const convertedQuantityReceived =
            line.quantityReceived && line.unitRatio && item?.orderingUnitRatio
              ? convertCountingUnitToOrderingUnit(
                  line.quantityReceived,
                  line.unitRatio,
                  item.orderingUnitRatio
                )
              : 0

          acc[line.itemId] = prev + convertedQuantityReceived
        })

        return acc
      },
      {}
    )

    const totalBudgetSalesAmount =
      get().predictedOrderUi?.totalBudgetSalesAmount ?? 0

    const groupedPredictedOrdeLines = transformToGroupedPredictedOrderLines(
      totalSalesAmount,
      totalBudgetSalesAmount,
      additionalBuffer,
      items,
      units,
      currentStockOnHandLines,
      openingQuantityOnHandDict,
      closingQuantityOnHandDict,
      quantityReceivedDict
    )

    // We deliberately don't expose a setter function outside this slice
    // to maintain data encapsulation. Components cannot directly modify the data
    set((state) => ({
      predictedOrderUi: {
        ...state.predictedOrderUi,
        groupedPredictedOrderLines: groupedPredictedOrdeLines,
        totalSalesAmount,

        predictedOrderRequestParams: {
          ...(state.predictedOrderUi?.predictedOrderRequestParams ?? {}),
          additionalBuffer,
          stocktakeTemplateId,
          salesModelStartDate,
          salesModelEndDate,
        } satisfies PredictedOrderRequestParams,
      },
    }))

    // Manually caching API responses in a 3-level nested dictionary (3D map):
    // This allows us to avoid redundant API calls by reusing previously fetched data
    // when the same combination of keys is requested again.
    //
    // We opted for a manual cache structure here to maintain fine-grained control over
    // how and when data is stored and invalidated. While tools like TanStack Query could
    // handle this automatically, our custom approach gives us better control over
    // cache keys and merging behavior.
    set((state) => {
      const updatePredictedOrderApiResponses = {
        predictedOrderApiResponses: {
          ...state.predictedOrderApiResponses,

          shouldRevalidate: false,

          posOrdersApiResponses: {
            ...state.predictedOrderApiResponses?.posOrdersApiResponses,

            [openingDateKey]: {
              ...state.predictedOrderApiResponses?.posOrdersApiResponses?.[
                openingDateKey
              ],
              [closingDateKey]: posOrders,
            },
          },

          stockReceivedApiResponses: {
            ...state.predictedOrderApiResponses?.stockReceivedApiResponses,

            [openingDateKey]: {
              ...state.predictedOrderApiResponses?.stockReceivedApiResponses?.[
                openingDateKey
              ],
              [closingDateKey]: stockReceived,
            },
          },

          stockOnHandReportApiResponses: {
            ...state.predictedOrderApiResponses?.stockOnHandReportApiResponses,

            [currentDateKey]: {
              ...state.predictedOrderApiResponses
                ?.stockOnHandReportApiResponses?.[currentDateKey],
              [stocktakeTemplateId]: currentStockOnHandReport,
            },

            [openingDateKey]: {
              ...state.predictedOrderApiResponses
                ?.stockOnHandReportApiResponses?.[openingDateKey],
              [stocktakeTemplateId]: openingStockOnHandReport,
            },

            [closingDateKey]: {
              ...state.predictedOrderApiResponses
                ?.stockOnHandReportApiResponses?.[closingDateKey],
              [stocktakeTemplateId]: closingStockOnHandReport,
            },
          },
        } satisfies PredictedOrderApiResponses,
      }

      return updatePredictedOrderApiResponses
    })

    return
  },

  updateIsLineDeleted: (lineId, deleted) => {
    set((state) => {
      const predictedOrderUi = state.predictedOrderUi ?? {
        groupedPredictedOrderLines: [],
        totalBudgetSalesAmount: undefined,
        totalSalesAmount: undefined,
        predictedOrderRequestParams: undefined,
      }

      const updatedGroups = predictedOrderUi.groupedPredictedOrderLines.map(
        (group) => ({
          ...group,
          lines: group.lines.map((line) =>
            line.lineId === lineId ? { ...line, isDeleted: deleted } : line
          ),
        })
      )

      return {
        predictedOrderUi: {
          ...predictedOrderUi,
          groupedPredictedOrderLines: updatedGroups,
        },
      }
    })
  },

  updateAllQuantitiesToOrder: (useMinimumStock: boolean) => {
    set((state) => {
      const predictedOrderUi = state.predictedOrderUi ?? {
        groupedPredictedOrderLines: [],
        totalBudgetSalesAmount: undefined,
        totalPredictedSalesAmount: undefined,
        totalSalesAmount: undefined,
        predictedOrderRequestParams: undefined,
      }

      const updatedGroups = predictedOrderUi.groupedPredictedOrderLines.map(
        (group: GroupedPredictedOrderLines) => ({
          ...group,
          lines: group.lines.map((line): IPredictedOrderLine => {
            const quantityToOrder = calculateQuantityToOrderDetails(
              line.predictedUsage,
              line.stockOnHandLine.quantityOnHand ?? 0,
              line.stockOnHandLine.minimumStockOnHandQuantity ?? 0,
              useMinimumStock
            )

            return {
              ...line,
              initialCurrentQuantityToOrder: quantityToOrder,
              currentQuantityToOrder: quantityToOrder,
            }
          }),
        })
      )

      return {
        predictedOrderUi: {
          ...predictedOrderUi,
          groupedPredictedOrderLines: updatedGroups,
        },
      }
    })
  },

  updateMinimumStock: async (lineId, updatedItem) => {
    // Keep a reference to the previous state
    const previousState = get().predictedOrderUi

    const minimumStockOnHand = convertCountingUnitToOrderingUnit(
      updatedItem.minimumStockOnHandQuantity,
      updatedItem.minimumStockOnHandUnit?.ratio,
      updatedItem.orderingUnitRatio
    )

    try {
      // Optimistic UI update
      set((state) => {
        const predictedOrderUi = state.predictedOrderUi ?? {
          groupedPredictedOrderLines: [],
          totalBudgetSalesAmount: undefined,
          totalPredictedSalesAmount: undefined,
          totalSalesAmount: undefined,
          predictedOrderRequestParams: undefined,
        }

        const updatedGroups = predictedOrderUi.groupedPredictedOrderLines.map(
          (group: GroupedPredictedOrderLines) => ({
            ...group,
            lines: group.lines.map((line): IPredictedOrderLine => {
              if (line.lineId !== lineId) return line

              const currentQuantityToOrder = calculateQuantityToOrderDetails(
                line.predictedUsage,
                line?.stockOnHandLine?.quantityOnHand ?? 0,
                minimumStockOnHand
              )

              return {
                ...line,
                stockOnHandLine: {
                  ...line.stockOnHandLine,
                  minimumStockOnHandQuantity: minimumStockOnHand,

                  minimumStockOnHandUnitId:
                    updatedItem.minimumStockOnHandUnitId,
                },

                initialCurrentQuantityToOrder: currentQuantityToOrder,
                currentQuantityToOrder: currentQuantityToOrder,
              }
            }),
          })
        )

        return {
          predictedOrderUi: {
            ...predictedOrderUi,
            groupedPredictedOrderLines: updatedGroups,
          },
        }
      })

      const updatedItemResponse = await useStockStore
        .getState()
        .updateItem(updatedItem)

      if (!updatedItemResponse) {
        // Revert the state update if the service call failed
        set({ predictedOrderUi: previousState })
        throw new Error('The minimum stock could not be updated for this item')
      }
    } catch (error) {
      exceptionMessageHandler(error)
    }
  },

  updateQuantityToOrder: (lineId, currentQuantityToOrder) => {
    set((state) => {
      const ui = state.predictedOrderUi ?? {
        groupedPredictedOrderLines: [],
        totalBudgetSalesAmount: undefined,
        totalSalesAmount: undefined,
        predictedOrderRequestParams: undefined,
      }

      const updatedGroups = ui.groupedPredictedOrderLines.map((group) => ({
        ...group,
        lines: group.lines.map((line) =>
          line.lineId === lineId ? { ...line, currentQuantityToOrder } : line
        ),
      }))

      return {
        predictedOrderUi: {
          ...ui,
          groupedPredictedOrderLines: updatedGroups,
        },
      }
    })
  },

  addStockcount: async (newStockTake, currentPredictedOrderLine) => {
    try {
      const singleStockTake = await useStockStore
        .getState()
        .createStocktake(newStockTake)

      if (!singleStockTake) return

      const items = await useStockStore.getState().getItems()
      const itemId = currentPredictedOrderLine.stockOnHandLine.itemId
      const orderingUnitRatio =
        itemId && items && items[itemId]?.orderingUnitRatio

      if (!orderingUnitRatio) return

      const updatedStockOnHandLine = updateStockOnHandLineWithStocktake(
        singleStockTake,
        currentPredictedOrderLine.stockOnHandLine,
        orderingUnitRatio
      )

      if (!updatedStockOnHandLine) return

      // Find the predicted order line that matches the given lineId,
      // and update its stockOnHandLine with the new value.
      // Preserve all other lines, groups, and UI state as-is.
      set((state) => {
        const prevPredictedOrderUi = state.predictedOrderUi ?? {
          groupedPredictedOrderLines: [],
        }

        const prevPredictedOrderApiResponses =
          state.predictedOrderApiResponses ?? {
            groupedPredictedOrderLines: [],
          }

        const updatedGroupedLines: GroupedPredictedOrderLines[] =
          prevPredictedOrderUi.groupedPredictedOrderLines.map((group) => ({
            ...group,
            lines: group.lines.map((line) => {
              if (line.lineId === currentPredictedOrderLine.lineId) {
                return {
                  ...line,
                  stockOnHandLine: updatedStockOnHandLine,
                }
              }
              return line
            }),
          }))

        return {
          predictedOrderUi: {
            ...prevPredictedOrderUi,
            groupedPredictedOrderLines: updatedGroupedLines,
          },

          predictedOrderApiResponses: {
            ...prevPredictedOrderApiResponses,
            shouldRevalidate: true,
          },
        }
      })
    } catch (error) {
      exceptionMessageHandler(error)
    }
  },
})

export default createPredictedOrderSlice
